import {Component, NgModule, OnDestroy, OnInit} from '@angular/core';
import {select, Store} from "@ngrx/store";
import { loadBillings, loadInvoice, clearInvoice } from "../../../state/billing/billing.actions";
import {Observable, Subscription} from "rxjs";
import {
  selectBillingError,
  selectBillingSuccess,
  selectInvoiceSuccess,
  selectInvoiceError
} from "../../../state/billing/billing.selectors";
import {AuthService} from "../../../services/auth.service";
import {HeaderService} from "../../../services/header.service";
import {DocumentViewerService} from "../../../services/document-viewer.service";
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss',
})

export class InvoicesComponent implements OnInit, OnDestroy {

  private subscriptions = new Subscription();
  private invoiceSubscriptions = new Subscription(); //

  user: any;

  invoices: any[] = [];
  pagination: any = {
    current_page: 1,
    from: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  }

  constructor(
    private store: Store,
    private authService: AuthService,
    private headerService: HeaderService,
    private documentViewerService: DocumentViewerService
  ) {}

  ngOnInit(): void {

    this.headerService.setHeaderState('Invoices', null, true);

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    this.getInvoices();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.invoiceSubscriptions.unsubscribe(); // Unsubscribe when component is destroyed
  }

  getInvoices(page: number = 1) {

    const userId = this.user.id

    this.store.dispatch(loadBillings({ userId: userId, page: page }));

    this.subscriptions.add(
      this.store.pipe(select(selectBillingSuccess)).subscribe(
        success => {
          if (success) {
            this.onLoadBillingsSuccess(success);
          }
        }
      )
    );

    this.subscriptions.add(
      this.store.pipe(select(selectBillingError)).subscribe(
        error => {
          if (error) {
            this.onLoadBillingsFailure(error);
          }
        }
      )
    );

  }

  /**
   * Page change.
   * This function is triggered when the user changes the page.
   */
  onPageChanged(page: number): void {
    this.getInvoices(page)
  }

  viewInvoice(invoiceId: number) {
    console.log('viewInvoice', invoiceId);

    // Clear old invoice data from store to prevent showing cached invoice
    this.store.dispatch(clearInvoice());

    // Clear any existing invoice subscriptions before adding new ones
    this.invoiceSubscriptions.unsubscribe();
    this.invoiceSubscriptions = new Subscription();

    // Subscribe to success and error BEFORE dispatching the action
    // Use filter to ignore null/undefined and take(1) to auto-unsubscribe after first emission
    this.invoiceSubscriptions.add(
      this.store.pipe(
        select(selectInvoiceSuccess),
        filter(success => !!success), // Only proceed if success is truthy
        take(1) // Take only the first emission and auto-unsubscribe
      ).subscribe(
        success => {
          this.onLoadInvoiceSuccess(success);
        }
      )
    );

    this.invoiceSubscriptions.add(
      this.store.pipe(
        select(selectInvoiceError),
        filter(error => !!error), // Only proceed if error is truthy
        take(1) // Take only the first emission and auto-unsubscribe
      ).subscribe(
        error => {
          this.onLoadInvoiceFailure(error);
        }
      )
    );

    // Dispatch action AFTER setting up subscriptions
    this.store.dispatch(loadInvoice({ invoiceId }));
  }

  onLoadBillingsSuccess(data: any): void {
    // console.log('Billing data loaded successfully:', data);
    this.invoices = data;

    this.invoices = data.data || [];
    this.pagination = { ...data }; // Create a new object to trigger change detection

  }

  onLoadBillingsFailure(error: any): void {
    console.error('Failed to load billing data:', error);
  }

  onLoadInvoiceSuccess(data: any): void {
    console.log('Invoice loaded successfully:', data);

    // Extract base64 PDF data from the response structure
    // API response: { data: { invoice_data: "base64string" }, metadata: { message: "..." } }
    const base64PDF = data?.data?.invoice_data || data?.invoice_data;

    if (!base64PDF) {
      console.error('Failed to open invoice: No invoice data found in response');
      alert('Failed to load invoice. Please try again.');
      return;
    }

    // Use the DocumentViewerService to open the PDF in a new tab
    const result = this.documentViewerService.viewBase64PDF(base64PDF, {
      openInNewWindow: true,
      windowName: '_blank'
    });

    if (!result.success) {
      console.error('Failed to open invoice:', result.error);
      alert(`Failed to open invoice: ${result.error || 'Unknown error'}`);
    }
  }

  onLoadInvoiceFailure(error: any): void {
    console.error('Failed to load invoice:', error);
    const errorMessage = error?.message || error?.error?.message || 'Failed to load invoice. Please try again.';
    alert(errorMessage);
  }

}
